import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, EyeOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, shopDateTime, roleLabel } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function AuditLog() {
  const { can } = useAuth();
  const canRemove = can("audit:remove");
  const canSeeAll = can("audit:read-all");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [showRemoved, setShowRemoved] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api
      .get("/audit-log", { params: { limit: 200, show_removed: showRemoved } })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  }, [showRemoved]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.post(`/audit-log/${id}/remove`, { reason: "" });
      toast.success("Removed from the trail view — still kept in the backend");
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not remove that entry");
    } finally {
      setBusy("");
    }
  };

  const restore = async (id) => {
    setBusy(id);
    try {
      await api.post(`/audit-log/${id}/restore`);
      toast.success("Restored to the trail view");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not restore that entry");
    } finally {
      setBusy("");
    }
  };

  const visible = rows.filter((r) =>
    `${r.action} ${r.summary || ""} ${r.by || ""}`.toLowerCase().includes(filter.toLowerCase())
  );
  const showActions = canRemove || canSeeAll;

  return (
    <div className="fade-up space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-3xl font-bold sm:text-4xl">Audit Trail</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every invoice, rate change, void and settings edit — who did it and when.
          {canRemove &&
            " Removing an entry only hides it from this view; it stays in the backend and in backups."}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Input
          className="h-10 max-w-sm rounded-none"
          placeholder="Filter by action, person or detail"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="audit-filter-input"
        />
        {canSeeAll && (
          <label
            className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground"
            data-testid="audit-show-removed-toggle"
          >
            <input
              type="checkbox"
              checked={showRemoved}
              onChange={(e) => setShowRemoved(e.target.checked)}
              data-testid="audit-show-removed-checkbox"
            />
            Show removed entries
          </label>
        )}
      </div>

      <div className="overflow-x-auto border border-border">
        <table className="w-full min-w-[760px] text-sm" data-testid="audit-table">
          <thead className="bg-[#F8F9FA]">
            <tr className="border-b border-border">
              <th className="px-3 py-2.5 text-left font-semibold">When</th>
              <th className="px-3 py-2.5 text-left font-semibold">Action</th>
              <th className="px-3 py-2.5 text-left font-semibold">Detail</th>
              <th className="px-3 py-2.5 text-left font-semibold">By</th>
              <th className="px-3 py-2.5 text-left font-semibold">Role</th>
              {showActions && <th className="px-3 py-2.5 text-right font-semibold">&nbsp;</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visible.length === 0 && (
              <tr>
                <td
                  colSpan={showActions ? 6 : 5}
                  className="px-3 py-10 text-center text-muted-foreground"
                >
                  <ShieldCheck className="mx-auto mb-2 h-5 w-5 text-[#C5A059]" />
                  No activity recorded yet.
                </td>
              </tr>
            )}
            {visible.map((r) => (
              <tr
                key={r.id}
                className={`hover:bg-[#FBF8F2] ${r.hidden ? "bg-[#FBF5EC] opacity-70" : ""}`}
                data-testid={`audit-row-${r.id}`}
              >
                <td className="tabular px-3 py-2 text-xs">{shopDateTime(r.at)}</td>
                <td className="px-3 py-2 font-semibold">{r.action}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {r.summary || r.reason || r.entity_id || "-"}
                  {r.hidden && (
                    <span
                      className="ml-2 inline-block bg-[#C5A059]/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[#8A6D2F]"
                      data-testid={`audit-removed-badge-${r.id}`}
                    >
                      Removed{r.hidden_by ? ` by ${r.hidden_by}` : ""}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">{r.by}</td>
                <td className="px-3 py-2 text-xs uppercase tracking-wider">{roleLabel(r.by_role)}</td>
                {showActions && (
                  <td className="px-3 py-2 text-right">
                    {r.hidden
                      ? canSeeAll && (
                          <Button
                            variant="outline"
                            className="h-8 rounded-none text-xs"
                            disabled={busy === r.id}
                            onClick={() => restore(r.id)}
                            data-testid={`audit-restore-${r.id}`}
                          >
                            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                          </Button>
                        )
                      : canRemove &&
                        (confirmId === r.id ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            Remove?
                            <Button
                              className="h-8 rounded-none bg-[#B4232A] text-xs text-white hover:bg-[#9A1E24]"
                              disabled={busy === r.id}
                              onClick={() => remove(r.id)}
                              data-testid={`audit-remove-confirm-${r.id}`}
                            >
                              Yes
                            </Button>
                            <Button
                              variant="outline"
                              className="h-8 rounded-none text-xs"
                              onClick={() => setConfirmId(null)}
                              data-testid={`audit-remove-cancel-${r.id}`}
                            >
                              No
                            </Button>
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            className="h-8 rounded-none text-xs"
                            onClick={() => setConfirmId(r.id)}
                            data-testid={`audit-remove-${r.id}`}
                          >
                            <EyeOff className="mr-1 h-3.5 w-3.5" /> Remove
                          </Button>
                        ))}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
